import React, { createContext, useEffect, useRef } from 'react';

import useApi from '../../hooks/useApi';
import {
  extractOfferMainDetails,
  extractOfferMainDetailsV2,
  extractProgressBarMainDetails,
} from '../../utils';
import {
  EEventsType,
  EPublisherWebhookEventType,
  EPublisherWebhookEventTypeV2,
  EPublisherWebhookOfferEventType,
  EPublisherWebhookOfferEventTypeV2,
  EStorePhase,
} from '../../constants/enums';
import { OfferEventData, OfferEventDataV2 } from '../../constants/apiRequest.types';
import { OfferData } from '../../constants/apiResponses.types';
import useCustomEvents from 'hooks/useCustomEvents';
import {
  arrangeMixpanelData,
  partitionOffersByType,
} from './EventBatchProvider.utils';

interface EventContextType {
  sendEvent: (event: OfferData, identifier: string) => void;
  resetObservedOffers: () => void;
}
const observedOffers = [] as string[];

export const EventBatchContext = createContext<EventContextType | undefined>(undefined);

export const EventBatchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const eventsRef = useRef<OfferData[]>([]);
  const customEvents = useCustomEvents();
  const API = useApi({});
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceDelay = 1000;

  const sendEvent = (data: OfferData, identifier: string) => {
    if (observedOffers.includes(identifier)) return;

    observedOffers.push(identifier);
    eventsRef.current.push(data);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (eventsRef.current.length === 0) return;

      const tabs = API.getOffers.data?.tabs;
      const { offers, progressBars } = partitionOffersByType(eventsRef.current);

      customEvents.sendCustomEvent(
        EEventsType.OFFERS_SHOWN,
        {
          offers: arrangeMixpanelData(eventsRef.current, tabs),
        },
        EStorePhase.POST_LOGIN
      );
      API.sendPublisherWebhookEvent.mutate({
        eventType: EPublisherWebhookEventType.OFFER,
        data: {
          eventName: EPublisherWebhookOfferEventType.OFFERS_SHOWN,
          offers: extractOfferMainDetails(offers, tabs),
          ...customEvents.getEventsSharedProps(),
        } as OfferEventData,
      });

      API.sendPublisherWebhookEventV2.mutate({
        eventType: EPublisherWebhookEventTypeV2.OFFERS,
        data: {
          eventName: EPublisherWebhookOfferEventTypeV2.WEBSTORE_OFFERS_DISPLAYED,
          offers: extractOfferMainDetailsV2(offers, tabs),
          progressBars: extractProgressBarMainDetails(progressBars),
          ...customEvents.getEventsSharedProps(),
        } as OfferEventDataV2,
      });
      eventsRef.current = [];
    }, debounceDelay);
  };

  const resetObservedOffers = () => {
    observedOffers.length = 0;
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <EventBatchContext.Provider value={{ sendEvent, resetObservedOffers }}>
      {children}
    </EventBatchContext.Provider>
  );
};
